import { toast } from 'react-toastify';
import useOrgChartState, { IOrgChart } from '@/states/organization.state';
import useTenantState from '@/states/tenant.state';

interface HandlerProps {
  setIsLoading: (val: boolean) => void;
  setTreeData: (data: any) => void;
  setFormMode: (mode: any) => void;
  setSelectedNode: (node: any) => void;
  organizationId?: number;
}

export const useOrgChartHandler = ({ setIsLoading, setTreeData, setFormMode, setSelectedNode, organizationId }: HandlerProps) => {
  const { currentTenant } = useTenantState();
  const { fetchOrgChartTree, createOrgChart, createChildOrgChart, updateOrgChart } = useOrgChartState();

  const transformToD3Tree = (node: IOrgChart | null | undefined): any => {
    if (!node || !node.name) return null;

    return {
      name: node.name || 'Untitled',
      description: node.description || '',
      id: node.id,
      children: Array.isArray(node.children) ? node.children.map(transformToD3Tree).filter(Boolean) : [],
    };
  };

  const handleSave = async (data: IOrgChart, mode: 'add' | 'edit' | 'add-child', parentId?: number) => {
    if (!currentTenant?.id) return;
    setIsLoading(true);

    try {
      if (mode === 'add') {
        await createOrgChart({
          name: data.name,
          description: data.description,
          is_published: true,
          organization_id: organizationId,
        });
      } else if (mode === 'add-child' && parentId) {
        await createChildOrgChart(parentId, {
          name: data.name,
          description: data.description,
          is_published: true,
          organization_id: organizationId,
        });
      } else if (mode === 'edit' && data.id) {
        await updateOrgChart(data.id, {
          name: data.name,
          description: data.description,
          is_published: true,
          organization_id: organizationId || data.organization_id,
        });
      }

      const updated = await fetchOrgChartTree(currentTenant.id, organizationId);

      if (updated) {
        const tree = Array.isArray(updated) ? updated.map(transformToD3Tree) : transformToD3Tree(updated);
        setTreeData(tree);
      }

      setFormMode(null);
      setSelectedNode(null);
    } catch (err) {
      console.error('Failed to save org chart:', err);
      toast.error('Failed to save org chart');
    } finally {
      setIsLoading(false);
    }
  };

  return { handleSave, transformToD3Tree };
};
